<template>
	<div id="MobileMain">
		<!-- QR 바코드북 -->
		<img class="qr" src="@/assets/images/qr-main.png" alt="QR 이벤트" @click="goToQrEvent" />

		<!-- 히어로 -->
		<section class="hero">
			<span class="label">LAB MANAGER STORE</span>
			<h1>실험에 필요한 모든 것을<br /><mark>가장 저렴하게</mark></h1>
			<p class="sub">연구실 구매, 이제 한 곳에서</p>
			<div class="cta">
				<button class="btn-yellow" @click="goChemical()">화학물질 둘러보기 →</button>
				<button class="btn-outline" @click="goSupplies()">실험용품/기기 둘러보기 →</button>
			</div>
		</section>

		<!-- 입점 브랜드 -->
		<section class="brands" v-if="heroBrands.length > 0">
			<div class="brands-head">
				<b>입점 브랜드</b>
				<button @click="goBrandPage">전체 보기 →</button>
			</div>
			<div class="logos">
				<button class="logo" v-for="brand in heroBrands" :key="brand.id" @click="goBrand(brand.id)">
					<img :src="brand.image" alt="" />
				</button>
			</div>
		</section>

		<!-- 이벤트 배너 -->
		<div class="banner-wrapper" v-if="banners && banners.length > 0">
			<banners />
		</div>

		<!-- 무엇을 찾으세요? -->
		<section class="finder">
			<h2>무엇을 찾으세요?</h2>
			<div class="card chemical">
				<div class="head">
					<b>화학물질</b>
					<span>시약 · 표준품 · 바이오</span>
				</div>
				<div class="chips">
					<button v-for="tag in chemicalTags" :key="tag" @click="goChemical(tag)">{{ tag }}</button>
				</div>
				<form class="quick-search" @submit.prevent="searchChemical">
					<input v-model.trim="chemicalKeyword" type="text" placeholder="CAS No, 시약명, 제품번호" />
					<button type="submit">검색</button>
				</form>
			</div>
			<div class="card supplies">
				<div class="head">
					<b>실험용품/기기</b>
					<span>
						소모품 · 안전용품 · 장비
						<template v-if="suppliesTotal > 0">· 총 {{ suppliesTotal.toLocaleString('ko-KR') }}개</template>
					</span>
				</div>
				<div class="chips">
					<button v-for="category in supplyCategories" :key="category.idx" @click="goSupplies(category.idx)">
						{{ category.name }}
					</button>
				</div>
				<form class="quick-search" @submit.prevent="searchSupplies">
					<input v-model.trim="suppliesKeyword" type="text" placeholder="상품명, 브랜드" />
					<button type="submit">검색</button>
				</form>
			</div>
		</section>

		<!-- 강점 -->
		<section class="points">
			<h2>랩매니저 스토어가 특별한 이유</h2>
			<ul>
				<li v-for="reason in reasons" :key="reason.no">
					<span class="no">{{ reason.no }}</span>
					<div>
						<b>{{ reason.title }}</b>
						<p>{{ reason.desc }}</p>
					</div>
				</li>
			</ul>
		</section>

		<modal-popup :list="popupListFilter" />
	</div>
</template>

<script>
import Banners from '@/views/main/components/Banners.vue'
import ModalPopup from '@/views/main/modal/ModalPopup.vue'
import cookies from 'vue-cookies'
import { mapActions, mapGetters, mapState } from 'vuex'
import { ToastType } from '@/utils/define/ToastType'
import gaAPI from '@/utils/ga/api'
import suppliesAPI from '@/services/api/supplies'
import PageName from '@/utils/define/PageName'

// PC 메인(views/main/Main.vue)과 같은 데이터를 쓰고, 화면 구성만 모바일용으로 다시 만든 화면.
export default {
	name: 'MobileMain',
	components: { Banners, ModalPopup },
	data() {
		return {
			chemicalTags: ['시약', '표준품', '바이오'],
			chemicalKeyword: '',
			suppliesKeyword: '',
			supplyCategories: [],
			reasons: [
				{ no: '01', title: '온라인 최저가로 연구비 부담 ↓', desc: 'Merck 제품 10% 할인을 비롯해 국내 브랜드 제품 최대 25% 할인까지, 다양한 할인 혜택으로 연구 비용을 절감할 수 있습니다.' },
				{ no: '02', title: '제조사가 직접! 안전하고 빠른 배송', desc: '주문 확인부터 출고, 배송까지 제조사의 관리 아래 진행되어 더 빠르게 상품을 받아볼 수 있습니다.' },
				{ no: '03', title: '실시간 견적 출력, 월 1회 통합 결제서류', desc: '번거로운 견적 문의 없이 견적서를 바로 출력하고, 월 1회 통합 결제서류로 편리하게 정산할 수 있습니다.' },
				{ no: '04', title: '문의는 채팅 상담으로 즉시 해결!', desc: '재고, 수량부터 배송 일정, 주문 관련 문의까지 실시간 채팅 상담으로 확인할 수 있습니다.' },
			],
		}
	},
	computed: {
		...mapState('main', ['banners']),
		...mapState('brand', ['brandImageList']),
		...mapGetters('user', ['instituteId', 'instituteName', 'userInfo']),
		...mapState('popup', ['popupList']),
		heroBrands() {
			return (this.brandImageList || []).slice(0, 12)
		},
		suppliesTotal() {
			return this.supplyCategories.reduce((sum, v) => sum + v.count, 0)
		},
		popupListFilter() {
			return this.popupList.filter(popup => !cookies.isKey(`popup_${popup.id}`))
		},
	},
	watch: {
		instituteId: {
			handler(newVal, oldVal) {
				if (!newVal || oldVal) return
				gaAPI.gaEventLogin({
					institute_id: this.instituteId,
					institute_name: this.instituteName,
					lab_id: this.instituteId,
					lab_name: this.instituteName,
					user_id: this.userInfo?.id,
				})
			},
			immediate: true,
		},
	},
	created() {
		this.fetchPopupList()
	},
	async mounted() {
		this.lookupBanners()
		this.lookupBrandImageList()
		this.loadSupplyCategories()
		if (this.$route.params.isFromStart) await this.getInstitute()
	},
	methods: {
		...mapActions('user', ['getInstitute']),
		...mapActions('main', ['lookupBanners']),
		...mapActions('brand', ['lookupBrandImageList']),
		...mapActions('popup', ['lookupPopupList']),
		async loadSupplyCategories() {
			try {
				const { data } = await suppliesAPI.lookupCategories()
				this.supplyCategories = data.filter(v => v.count > 0)
			} catch (e) {
				this.supplyCategories = []
			}
		},
		async fetchPopupList() {
			try {
				await this.lookupPopupList()
				if (this.popupListFilter.length > 0) this.$bvModal.show('ModalPopup')
			} catch (e) {
				this.$root.toast('에러', e.response?.data?.msg, ToastType.ERROR)
			}
		},
		goChemical(tag) {
			const query = tag ? { tags: tag } : { classification: '' }
			this.$router.push({ path: '/search', query }).catch(() => {})
		},
		goSupplies(parentIdx) {
			this.$router.push({ name: PageName.Supplies, query: parentIdx ? { p: String(parentIdx) } : {} }).catch(() => {})
		},
		searchChemical() {
			if (!this.chemicalKeyword) return this.goChemical()
			this.$router.push({ path: '/search', query: { keyword: this.chemicalKeyword, type: 'ALL' } }).catch(() => {})
		},
		searchSupplies() {
			this.$router.push({ name: PageName.Supplies, query: this.suppliesKeyword ? { q: this.suppliesKeyword } : {} }).catch(() => {})
		},
		goBrand(id) {
			this.$router.push({ path: '/search/brand', query: { id } }).catch(() => {})
		},
		goBrandPage() {
			this.$router.push({ name: PageName.List, params: { category: 'brand' } }).catch(() => {})
		},
		goToQrEvent() {
			window.open('https://store.lab-manager.com/product/list/sale/1838668')
		},
	},
}
</script>

<style lang="scss" scoped>
$YELLOW: #fabf14;
$INK: #161616;

#MobileMain {
	padding: 12px 14px 0;
	h2 {
		margin: 0 0 14px;
		font-size: 1.15rem;
		font-weight: 800;
		color: $INK;
	}

	.qr {
		display: block;
		width: 100%;
		border-radius: 14px 14px 0 0;
		background: #fffbea;
	}

	/* 히어로: 크림색 카드 */
	.hero {
		position: relative;
		overflow: hidden;
		padding: 22px 18px 24px;
		border-radius: 0 0 14px 14px;
		background: #fffbea;
		&:before {
			content: '';
			position: absolute;
			top: -70px;
			right: -60px;
			width: 190px;
			height: 190px;
			border-radius: 50%;
			background: $YELLOW;
		}
		> * {
			position: relative;
		}
		.label {
			display: inline-block;
			padding: 4px 10px;
			border-radius: 12px;
			background: $YELLOW;
			font-size: 0.66rem;
			font-weight: 800;
			letter-spacing: 0.12em;
		}
		h1 {
			margin: 14px 0 8px;
			font-size: 1.65rem;
			line-height: 1.3;
			font-weight: 900;
			letter-spacing: -0.02em;
			mark {
				padding: 0;
				background: linear-gradient(transparent 62%, $YELLOW 62%);
				color: inherit;
			}
		}
		.sub {
			margin: 0 0 16px;
			font-size: 0.95rem;
			color: #4b5563;
		}
		.cta {
			display: flex;
			flex-direction: column;
			gap: 8px;
			button {
				height: 48px;
				border-radius: 24px;
				font-size: 0.95rem;
				font-weight: 800;
			}
			.btn-yellow {
				background: $YELLOW;
				color: $INK;
			}
			.btn-outline {
				border: 2px solid $INK;
				background: #fff;
				color: $INK;
			}
		}
	}

	/* 입점 브랜드 */
	.brands {
		margin-top: 14px;
		padding: 16px;
		border: 1px solid #eceef1;
		border-radius: 14px;
		background: #fff;
		.brands-head {
			display: flex;
			align-items: center;
			justify-content: space-between;
			margin-bottom: 12px;
			b {
				font-size: 0.98rem;
				font-weight: 800;
			}
			button {
				font-size: 0.82rem;
				color: #6b7280;
			}
		}
		.logos {
			display: grid;
			grid-template-columns: repeat(4, 1fr);
			gap: 8px;
			.logo {
				display: flex;
				align-items: center;
				justify-content: center;
				height: 46px;
				padding: 6px;
				border: 1px solid #eceef1;
				border-radius: 10px;
				background: #fff;
				img {
					max-width: 100%;
					max-height: 100%;
					object-fit: contain;
				}
			}
		}
	}

	/* 배너 */
	.banner-wrapper {
		margin-top: 14px;
		::v-deep .banner-container {
			height: 120px;
			border-radius: 12px;
			overflow: hidden;
			.banner-image {
				width: 100%;
				img {
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
			}
			.prev,
			.next {
				display: none;
			}
		}
	}

	/* 무엇을 찾으세요? */
	.finder {
		margin-top: 28px;
		.card {
			margin-bottom: 12px;
			padding: 16px;
			border: 1px solid #eceef1;
			border-radius: 14px;
			background: #fff;
			.head {
				margin-bottom: 10px;
				b {
					display: block;
					font-size: 1.02rem;
					font-weight: 800;
				}
				span {
					font-size: 0.78rem;
					color: #6b7280;
				}
			}
			.chips {
				display: flex;
				flex-wrap: wrap;
				gap: 6px;
				margin-bottom: 12px;
				button {
					height: 30px;
					padding: 0 12px;
					border: 1px solid #dfe3e8;
					border-radius: 15px;
					font-size: 0.8rem;
					color: #4b5563;
				}
			}
			.quick-search {
				display: flex;
				gap: 6px;
				input {
					flex: 1;
					min-width: 0;
					height: 42px;
					padding: 0 14px;
					border: 1px solid #dfe3e8;
					border-radius: 21px;
					font-size: 0.9rem;
				}
				button {
					flex: none;
					width: 62px;
					height: 42px;
					border-radius: 21px;
					background: $YELLOW;
					font-weight: 800;
				}
			}
		}
	}

	/* 강점 */
	.points {
		margin-top: 28px;
		ul {
			margin: 0;
			padding: 0;
			list-style: none;
		}
		li {
			display: flex;
			gap: 12px;
			margin-bottom: 10px;
			padding: 14px;
			border: 1px solid #eceef1;
			border-radius: 14px;
			background: #fff;
			.no {
				flex: none;
				width: 30px;
				height: 30px;
				border-radius: 50%;
				background: $YELLOW;
				font-size: 0.78rem;
				font-weight: 800;
				line-height: 30px;
				text-align: center;
			}
			b {
				display: block;
				margin-bottom: 4px;
				font-size: 0.92rem;
				font-weight: 800;
			}
			p {
				margin: 0;
				font-size: 0.82rem;
				line-height: 1.5;
				color: #6b7280;
			}
		}
	}
}
</style>
